import { lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Download, Scissors, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cragsService } from '../../services/crags';
import { usePhotoUpload } from '../../components/climbs/climb-editor/usePhotoUpload';
import type { CragModelViewerApi } from '../../components/crag/CragModelViewer';
import { FULL_CROP, type CropBox } from '../../components/crag/crop-box';
import UploadFileButton from '../components/UploadFileButton';
import CropPanel from '../components/crag-model/CropPanel';
import ModelBuilder from '../components/crag-model/ModelBuilder';
import { confirmLinesLost } from '../components/crag-model/confirm-lines-lost';
import { formatBytes, MAX_GLB_BYTES } from '../components/crag-model/format';
import { useCurrentCrag } from '../hooks/useCurrentCrag';

const CragModelViewer = lazy(() => import('../../components/crag/CragModelViewer'));

/** The cropped file's name: the model's own, marked once however often it is cut. */
function croppedName(filename: string | null | undefined, slug: string) {
  return `${(filename ?? `${slug}-3d.glb`).replace(/(-cropped)?\.glb$/i, '')}-cropped.glb`;
}

/**
 * The crag as a 3D model.
 *
 * Two halves. The top one is everywhere: the model the crag has, to look at,
 * crop, replace or drop — on production that is where a GLB built locally is
 * uploaded. Cropping is done here, in the browser (CragModelViewer, crop.ts):
 * the cut file replaces the crag's model like any upload. The bottom one is the generator, and exists on a development
 * server only (Object Capture and Blender run on the developer's Mac):
 * `import.meta.env.DEV` keeps it out of the built panel, and the server
 * answers 404 to its endpoints anywhere else.
 */
export default function CragModelPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { crag, loading, setCrag } = useCurrentCrag();
  const [saving, setSaving] = useState(false);
  /** Null when not cropping. */
  const [crop, setCrop] = useState<CropBox | null>(null);
  const [cropSaving, setCropSaving] = useState(false);
  const viewerApi = useRef<CragModelViewerApi | null>(null);
  // Active Storage's DirectUpload, as the climb editor sends a picture it made
  // in the browser: here the file is the cropped GLB, not one picked from disk.
  const { upload } = usePhotoUpload();

  if (loading) return <Skeleton className="h-96 w-full max-w-5xl" />;
  if (!crag) return null;

  const save = async (data: { model_3d_signed_id?: string; remove_model_3d?: boolean }) => {
    if (!confirmLinesLost(crag, t)) return;

    setSaving(true);
    try {
      setCrag(await cragsService.updateCrag(crag.slug, data));
      toast({ title: t(data.remove_model_3d ? 'cragModel.current.removed' : 'cragModel.current.uploaded') });
    } catch (error) {
      toast({
        title: t('cragModel.current.saveError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const saveCrop = async () => {
    if (!crop || !viewerApi.current) return;
    setCropSaving(true);
    try {
      const { blob, triangles, total } = await viewerApi.current.exportCropped(crop);
      if (blob.size > MAX_GLB_BYTES) throw new Error(t('cragModel.current.tooLarge'));
      const file = new File([blob], croppedName(crag.model_3d_filename, crag.slug), { type: 'model/gltf-binary' });
      const signedId = await upload(file);
      // A crop keeps the coordinates, so the routes drawn on the model stay put.
      setCrag(await cragsService.updateCrag(crag.slug, { model_3d_signed_id: signedId, keep_model_3d_frame: true }));
      setCrop(null);
      toast({ title: t('cragModel.crop.saved', { kept: triangles.toLocaleString(), total: total.toLocaleString() }) });
    } catch (error) {
      toast({
        title: t('cragModel.crop.saveError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setCropSaving(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('cragModel.current.title')}</CardTitle>
          <CardDescription>{t('cragModel.current.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {crag.model_3d_url ? (
            <Suspense fallback={<div className="h-[28rem] rounded-lg border bg-muted/40" />}>
              <CragModelViewer url={crag.model_3d_url} crop={crop} apiRef={viewerApi} />
            </Suspense>
          ) : (
            <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-lg border border-dashed text-sm text-muted-foreground">
              <Box className="h-6 w-6" />
              {t('cragModel.current.empty')}
            </div>
          )}

          {crop && (
            <CropPanel
              crop={crop}
              onChange={setCrop}
              onSave={saveCrop}
              onCancel={() => setCrop(null)}
              saving={cropSaving}
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            {crag.model_3d_filename && (
              <span className="text-sm">
                {crag.model_3d_filename} · {formatBytes(crag.model_3d_byte_size)}
              </span>
            )}
            <UploadFileButton
              accept=".glb,model/gltf-binary"
              maxBytes={MAX_GLB_BYTES}
              disabled={saving}
              onUploaded={(signedId) => save({ model_3d_signed_id: signedId })}
              onError={(reason) =>
                toast({
                  title: t(reason === 'too_large' ? 'cragModel.current.tooLarge' : 'cragModel.current.uploadError'),
                  variant: 'destructive',
                })
              }
            >
              {t(crag.model_3d_url ? 'cragModel.current.replace' : 'cragModel.current.upload')}
            </UploadFileButton>
            {crag.model_3d_url && (
              <>
                {!crop && (
                  <Button size="sm" variant="outline" className="gap-2" disabled={saving} onClick={() => setCrop(FULL_CROP)}>
                    <Scissors className="h-4 w-4" />
                    {t('cragModel.crop.start')}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="gap-2" asChild>
                  <a href={crag.model_3d_url} download={crag.model_3d_filename ?? undefined}>
                    <Download className="h-4 w-4" />
                    {t('cragModel.current.download')}
                  </a>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-2 text-destructive"
                  disabled={saving}
                  onClick={() => save({ remove_model_3d: true })}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('cragModel.current.remove')}
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {import.meta.env.DEV && <ModelBuilder crag={crag} onPublished={setCrag} />}
    </div>
  );
}
